import axios from "axios";
import { createMedicinePlan, listMedicinePlans, listMedicineTasks } from "./medicineService";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const apiMsg = (e) =>
  e?.response?.data?.message || e?.response?.data?.error ||
  (typeof e?.response?.data === "string" ? e.response.data : null) || e?.message || "Request failed";

const toISODate = (d) => {
  if (!d) return undefined;
  if (typeof d === "string") return d.slice(0, 10);
  try { return new Date(d).toISOString().slice(0, 10); } catch { return undefined; }
};

const toNumberOrNull = (v) => {
  if (v === "" || v === undefined || v === null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function client(path) {
  return axios.create({ baseURL: `${BASE_URL}${path}`, headers: { "Content-Type": "application/json", ...authHeaders() } });
}

function normalizeDiseaseCase(payload = {}) {
  return {
    batchId: toNumberOrNull(payload.batchId),
    animalId: toNumberOrNull(payload.animalId || payload.livestockId),
    livestockId: toNumberOrNull(payload.livestockId || payload.animalId),
    dateDetected: toISODate(payload.dateDetected),
    illnessType: payload.illnessType || payload.diseaseType || "INFECTIOUS",
    illnessName: payload.illnessName || payload.diseaseName || "FEVER",
    diseaseType: payload.illnessType || payload.diseaseType || "INFECTIOUS",
    diseaseName: payload.illnessName || payload.diseaseName || "FEVER",
    severity: payload.severity || "MILD",
    symptoms: Array.isArray(payload.symptoms) ? payload.symptoms : [],
    otherSymptoms: String(payload.otherSymptoms || payload.otherMeans || "").trim(),
    status: payload.status || "SUSPECTED",
    isolated: !!payload.isolated,
    isIsolated: !!payload.isolated,
    treatmentStarted: !!payload.treatmentStarted,
    treatmentStartDate: toISODate(payload.treatmentStartDate),
    treatmentTags: Array.isArray(payload.treatmentTags) ? payload.treatmentTags : [],
    treatmentNotes: String(payload.treatmentNotes || "").trim(),
    diseaseNotes: String(payload.diseaseNotes || payload.notes || "").trim(),
    notes: String(payload.notes || payload.diseaseNotes || "").trim(),
  };
}

export async function listBatchesIdName() {
  const res = await axios.get(`${BASE_URL}/api/public/batches/all`, { headers: authHeaders() });
  return Array.isArray(res.data) ? res.data : [];
}

export async function listLivestockByBatch(batchId) {
  if (!batchId) return [];
  const res = await axios.get(`${BASE_URL}/api/public/livestock/batch/${batchId}`, { headers: authHeaders() });
  return Array.isArray(res.data) ? res.data : [];
}

export async function listDiseaseCases({ batchId, animalId, assetId, status, from, to } = {}) {
  const { data } = await client("/api/disease-cases").get("", { params: { batchId, animalId, assetId, status, from: toISODate(from), to: toISODate(to) } });
  return Array.isArray(data) ? data : [];
}

export async function getDiseaseCase(id) {
  const { data } = await client("/api/disease-cases").get(`/${id}`);
  return data;
}

export async function createDiseaseCase(payload) {
  const { data } = await client("/api/disease-cases").post("", normalizeDiseaseCase(payload));
  return data;
}

export async function updateDiseaseCase(id, payload) {
  const { data } = await client("/api/disease-cases").put(`/${id}`, normalizeDiseaseCase(payload));
  return data;
}

export async function deleteDiseaseCase(id) {
  const { data } = await client("/api/disease-cases").patch(`/${id}/close`);
  return data;
}

export async function updateDiseaseStatus(id, status) {
  const { data } = await client("/api/disease-cases").patch(`/${id}/status`, { status });
  return data;
}

export async function createDiseaseWithTreatment(payload = {}) {
  const disease = await createDiseaseCase(payload.diseaseCase || payload);
  if (payload.medicinePlan?.medicineName) {
    await createMedicinePlan({ ...payload.medicinePlan, diseaseCaseId: disease.id, batchId: disease.batchId, animalId: disease.animalId });
  }
  return { diseaseCase: disease };
}

export async function getTreatmentSummary(id) {
  const diseaseCase = await getDiseaseCase(id);
  const medicinePlans = await listMedicinePlans(diseaseCase.batchId, id).catch(() => []);
  const medicineTasks = await listMedicineTasks({ diseaseCaseId: id }).catch(() => []);
  return { diseaseCase, medicinePlans, medicineTasks, medicineHistory: [] };
}

export async function addMedicinePlanToDisease(id, payload) {
  const disease = await getDiseaseCase(id);
  return createMedicinePlan({ ...payload, diseaseCaseId: id, batchId: disease.batchId, animalId: disease.animalId });
}

export async function generateDiseaseMedicineTasks() { return []; }
export async function upsertMedicalAction() { return null; }
export async function getMedicalActionByDiseaseCase() { return null; }
export async function deleteMedicalActionByDiseaseCase() { return null; }

export default {
  listBatchesIdName,
  listLivestockByBatch,
  listDiseaseCases,
  getDiseaseCase,
  createDiseaseCase,
  createDiseaseWithTreatment,
  updateDiseaseCase,
  deleteDiseaseCase,
  updateDiseaseStatus,
  getTreatmentSummary,
  addMedicinePlanToDisease,
  generateDiseaseMedicineTasks,
  upsertMedicalAction,
  getMedicalActionByDiseaseCase,
  deleteMedicalActionByDiseaseCase,
  _apiMsg: apiMsg,
};
